import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Settings as SettingsIcon, Loader2, CheckCircle2, XCircle, Mail, ShieldCheck, Trash2 } from "lucide-react";

export default function Settings() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ host: "ssl0.ovh.net", port: 465, username: "contact@pcup.fr", password: "", enabled: true });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const formReady = cfg !== null;

  const load = () => api.get("/settings/smtp").then((r) => {
    setCfg(r.data);
    setForm((f) => ({ ...f, host: r.data.host, port: r.data.port, username: r.data.username, enabled: r.data.enabled ?? true }));
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/smtp", { ...form, port: Number(form.port), password: form.password || undefined });
      setCfg(r.data);
      setForm((f) => ({ ...f, password: "" }));
      toast.success("Paramètres SMTP enregistrés");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur enregistrement"); } finally { setSaving(false); }
  };

  const test = async (withEmail) => {
    setTesting(true);
    try {
      const r = await api.post("/settings/smtp/test", { to: withEmail && testTo ? testTo : undefined });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec du test"); load(); } finally { setTesting(false); }
  };

  const remove = async () => {
    if (!confirm("Supprimer la configuration SMTP ? Les envois repasseront par la plateforme.")) return;
    await api.delete("/settings/smtp");
    toast.success("Configuration supprimée");
    setForm({ host: "ssl0.ovh.net", port: 465, username: "contact@pcup.fr", password: "", enabled: true });
    load();
  };

  const field = "mt-1.5 w-full px-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 focus:border-cyan-500/60 outline-none text-sm";

  return (
    <div className="p-6 lg:p-8 max-w-[900px]">
      <h1 className="font-heading text-3xl font-bold text-slate-100 flex items-center gap-3"><SettingsIcon className="w-7 h-7 text-cyan-400" /> Paramètres</h1>
      <p className="text-sm text-slate-400 mt-1">Connexion de votre boîte mail professionnelle pour envoyer depuis l'application.</p>

      <div className="mt-6 p-6 bg-[#121215] border border-slate-800/80 rounded-xl">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-cyan-400 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Boîte d'envoi SMTP (OVH)</div>
            <div className="font-heading text-lg font-semibold text-slate-100 mt-1">Envoyer depuis contact@pcup.fr avec suivi d'ouverture</div>
          </div>
          {cfg && (
            <span data-testid="smtp-status" className={`text-xs font-mono px-2.5 py-1 rounded-full border flex items-center gap-1.5 ${cfg.configured && cfg.enabled ? (cfg.last_test_ok === false ? "bg-rose-500/10 text-rose-300 border-rose-500/30" : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30") : "bg-slate-800 text-slate-400 border-slate-700"}`}>
              {cfg.configured && cfg.enabled ? (cfg.last_test_ok === false ? <><XCircle className="w-3.5 h-3.5" /> erreur au dernier test</> : <><CheckCircle2 className="w-3.5 h-3.5" /> actif</>) : "non configuré — envois via la plateforme"}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Adresse email (identifiant)</label>
            <input data-testid="smtp-username-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className={field} />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Serveur SMTP</label>
            <input data-testid="smtp-host-input" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} className={field} />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Port</label>
            <select data-testid="smtp-port-select" value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} className={field}>
              <option value={465}>465 — SSL (recommandé OVH)</option>
              <option value={587}>587 — STARTTLS</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Mot de passe de la boîte mail {cfg?.configured && <span className="text-slate-600 normal-case tracking-normal">(laisser vide pour conserver l'actuel)</span>}</label>
            <input data-testid="smtp-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={cfg?.configured ? "••••••••" : "Mot de passe OVH de contact@pcup.fr"} className={field} autoComplete="new-password" />
            <p className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Stocké chiffré côté serveur, jamais renvoyé au navigateur.</p>
          </div>
          <label className="md:col-span-2 flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input data-testid="smtp-enabled-checkbox" type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="accent-cyan-500" /> Utiliser cette boîte pour les envois depuis l'application
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-6">
          <button data-testid="smtp-save-button" onClick={save} disabled={saving || !formReady} className="px-4 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Enregistrer
          </button>
          <button data-testid="smtp-test-button" onClick={() => test(false)} disabled={testing || !cfg?.configured} className="px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 text-sm flex items-center gap-2 disabled:opacity-60">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Tester la connexion
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <input data-testid="smtp-test-to-input" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="votre@email.fr" className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 text-sm outline-none focus:border-cyan-500/60 w-56" />
            <button data-testid="smtp-send-test-button" onClick={() => test(true)} disabled={testing || !cfg?.configured || !testTo} className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 text-sm disabled:opacity-60">Envoyer un email test</button>
          </div>
        </div>
        {cfg?.last_error && <div data-testid="smtp-last-error" className="mt-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg p-3 font-mono break-all">{cfg.last_error}</div>}
        {cfg?.configured && (
          <button data-testid="smtp-delete-button" onClick={remove} className="mt-4 text-xs text-slate-500 hover:text-rose-400 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Supprimer la configuration</button>
        )}
      </div>

      <div className="mt-4 p-5 bg-slate-900/40 border border-slate-800/60 rounded-xl text-xs text-slate-400 leading-relaxed space-y-2">
        <div className="font-mono uppercase tracking-widest text-slate-500">Aide OVH</div>
        <p>Serveur <span className="text-slate-200">ssl0.ovh.net</span>, port <span className="text-slate-200">465 (SSL)</span> ou 587 (STARTTLS). Identifiant = adresse complète <span className="text-slate-200">contact@pcup.fr</span>, mot de passe = celui de la boîte (modifiable dans l'espace client OVH → Emails → Comptes).</p>
        <p>Règle d'usage : l'envoi depuis l'application reste réservé aux prospects qui vous ont communiqué leur email ou déjà en RDV / Gagné. Pour la prospection à froid, utilisez « Envoyer depuis ma messagerie » sur la fiche.</p>
      </div>
    </div>
  );
}
